import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  Divider,
  Container
} from '@mui/material';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../services/axiosInstance';

interface FieldDiagnosticsProps {}

interface AssetSearchResult {
  _id: string;
  title: string;
  fileName: string;
  categories: string[];
  metadataKeys: string[];
  metadataCount: number;
}

interface FieldAnalysis {
  key: string;
  value: any;
  valueType: string;
  valueLength: number | null;
  foundInTemplates: Array<{
    templateId: string;
    templateName: string;
    fieldId: string;
    fieldName: string;
    fieldLabel: string;
    fieldType: string;
  }>;
  isOrphaned: boolean;
}

interface AssetDetailedAnalysis {
  asset: {
    _id: string;
    title: string;
    fileName: string;
    fileType: string;
    categories: string[];
    uploadedAt: string;
    modifiedAt: string;
  };
  fieldAnalysis: {
    totalMetadataFields: number;
    orphanedFields: number;
    metadataFields: FieldAnalysis[];
    matchingTemplates: Array<{
      templateId: string;
      templateName: string;
      templateFieldCount: number;
      fields: Array<{
        id: string;
        name: string;
        label: string;
        type: string;
        hasValue: boolean;
        value: any;
        foundByFieldId?: boolean;
        foundByFieldName?: boolean;
        fieldIdValue?: any;
        fieldNameValue?: any;
      }>;
    }>;
  };
}

interface SystemHealth {
  totalAssets: number;
  totalTemplates: number;
  assetsWithMetadata: number;
  uniqueMetadataFields: number;
  uniqueTemplateFields: number;
  orphanedFieldsCount: number;
  orphanedFields: string[];
  healthScore: number;
}

interface FrontendUsageAnalysis {
  success: boolean;
  assetId: string;
  frontendUsageAnalysis: {
    assetDetails: {
      component: string;
      fieldSource: string;
      renderMethod: string;
      fieldsUsed: Array<{
        fieldKey: string;
        fieldName: string;
        fieldLabel: string;
        templateName: string;
        value: any;
        renderLocation: string;
        resolvedFrom?: string;
      }>;
      unusedMetadataKeys: string[];
    };
    collectionDetailView: {
      component: string;
      fieldSource: string;
      renderMethod: string;
      fieldsUsed: Array<{
        fieldKey: string;
        displayLabel: string;
        value: any;
        renderLocation: string;
        hasTemplateLabel: boolean;
      }>;
      unusedMetadataKeys: string[];
    };
    csvMapping: {
      component: string;
      fieldSource: string;
      renderMethod: string;
      fieldsUsed: Array<{
        csvFieldId: string;
        fieldName: string;
        fieldLabel: string;
        templateName: string;
        value: any;
        renderLocation: string;
      }>;
      unusedMetadataKeys: string[];
    };
  };
  summary: {
    assetDetailsFields: number;
    collectionViewFields: number;
    csvMappedFields: number;
    orphanedKeys: number;
  };
}

interface DataSourceAnalysis {
  success: boolean;
  assetId: string;
  dataSourceAnalysis: {
    probableDataSources: Array<{
      source: string;
      fieldCount: number;
      confidence: string;
    }>;
    fieldSourceAnalysis: Record<string, {
      fieldKey: string;
      currentValue: any;
      probableSource: string;
      confidence: string;
      indicators: string[];
    }>;
    timeline: Array<{
      timestamp: string;
      event: string;
      fieldsAdded?: number;
      fieldsModified?: string;
      source: string;
    }>;
  };
  summary: {
    totalFields: number;
    validooFields: number;
    csvFields: number;
    manualFields: number;
    unknownFields: number;
  };
}

interface DisplayMappingAnalysis {
  success: boolean;
  assetId: string;
  displayMappingAnalysis: {
    fieldDisplayMap: Record<string, {
      fieldKey: string;
      value: any;
      assetDetailsDisplay: {
        isVisible: boolean;
        displayLabel: string | null;
        component: string;
        renderMethod: string | null;
      };
      collectionViewDisplay: {
        isVisible: boolean;
        displayLabel: string | null;
        component: string;
        renderMethod: string;
      };
      csvExportDisplay: {
        isVisible: boolean;
        displayLabel: string | null;
        component: string;
        renderMethod: string;
      };
    }>;
    componentComparison: {
      differenceCount: number;
      onlyInAssetDetails: string[];
      onlyInCollectionView: string[];
      differentLabels: Array<{
        fieldKey: string;
        assetDetailsLabel: string | null;
        collectionViewLabel: string | null;
      }>;
      commonFields: string[];
    };
    renderingIssues: Array<{
      type: string;
      fieldKey: string;
      issue: string;
      impact: string;
    }>;
  };
  summary: {
    totalFields: number;
    visibleInAssetDetails: number;
    visibleInCollectionView: number;
    visibleInCsvExport: number;
    renderingIssues: number;
    componentDifferences: number;
  };
}

interface DebugMetadataAnalysis {
  success: boolean;
  assetId: string;
  assetInfo: {
    title: string;
    fileName: string;
    categories: string[];
  };
  rawMetadata: Record<string, any>;
  metadataKeys: string[];
  metadataKeysCount: number;
  matchingTemplates: Array<{
    templateId: string;
    templateName: string;
    categoryIds: string[];
    fields: Array<{
      fieldId: string;
      fieldName: string;
      fieldLabel: string;
      fieldType: string;
      valueFoundByFieldId: boolean;
      valueFoundByFieldName: boolean;
      actualValueByFieldId: any;
      actualValueByFieldName: any;
      resolvedValue: any;
      hasResolvedValue: boolean;
    }>;
  }>;
  fieldMatchingAnalysis: {
    totalMetadataKeys: number;
    templateFieldsCount: number;
    matchingFieldsCount: number;
    orphanedKeysCount: number;
  };
}

interface MappingConflictsAnalysis {
  success: boolean;
  mappingAnalysis: {
    totalMappings: number;
    uniqueTemplateFields: number;
    duplicates: number;
    duplicateDetails: Array<{
      templateField: string;
      validooFields: string[];
      count: number;
      riskLevel: 'HIGH' | 'MEDIUM' | 'LOW';
      mappingTypes: Array<{
        validooField: string;
        source: 'HARDCODED' | 'DROPDOWN' | 'BOTH';
        priority: number;
        isActiveInDropdown?: boolean;
        dropdownValue?: string;
      }>;
      recommendedAction: string;
    }>;
    cleanMappings: Array<{
      validooField: string;
      templateField: string;
      source: 'HARDCODED' | 'DROPDOWN';
    }>;
    priorityConflicts: Array<{
      templateField: string;
      hardcodedMapping: string;
      dropdownMapping?: string;
      currentWinner: 'HARDCODED' | 'DROPDOWN';
      recommendation: string;
    }>;
  };
  systemInfo: {
    template: string;
    templateId: string;
    enabledIntegrations: string[];
    totalTemplateFields: number;
  };
}

const FieldDiagnostics: React.FC<FieldDiagnosticsProps> = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AssetSearchResult[]>([]);
  const [selectedAssetAnalysis, setSelectedAssetAnalysis] = useState<AssetDetailedAnalysis | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [frontendUsageData, setFrontendUsageData] = useState<FrontendUsageAnalysis | null>(null);
  const [dataSourceData, setDataSourceData] = useState<DataSourceAnalysis | null>(null);
  const [displayMappingData, setDisplayMappingData] = useState<DisplayMappingAnalysis | null>(null);
  const [debugMetadataData, setDebugMetadataData] = useState<DebugMetadataAnalysis | null>(null);
  const [advancedAnalysisLoading, setAdvancedAnalysisLoading] = useState(false);
  const [mappingConflictsData, setMappingConflictsData] = useState<MappingConflictsAnalysis | null>(null);
  const [mappingConflictsLoading, setMappingConflictsLoading] = useState(false);

  // Validoo Probe states
  const [validooRawFields, setValidooRawFields] = useState<Array<{path: string, label: string, example: string}>>([]);
  const [validooComputed, setValidooComputed] = useState<Record<string, string>>({});
  const [validooLoading, setValidooLoading] = useState(false);
  const [validooFilter, setValidooFilter] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Ange en sökterm');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get(`/api/field-diagnostics/search/${encodeURIComponent(searchQuery)}`);
      
      if (response.data.success) {
        setSearchResults(response.data.assets);
        if (response.data.assets.length === 0) {
          setError('Inga assets hittades för din sökning');
        }
      } else {
        setError(response.data.message || 'Sökning misslyckades');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.response?.data?.message || 'Ett fel inträffade vid sökning');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const analyzeAsset = async (assetId: string) => {
    setLoading(true);
    setError(null);
    setSelectedAssetAnalysis(null);
    
    try {
      const response = await axiosInstance.get<AssetDetailedAnalysis>(`/api/field-diagnostics/asset/${assetId}`);
      setSelectedAssetAnalysis(response.data);
      
      await fetchAdvancedAnalysis(assetId);
    } catch (err: any) {
      setError(`Failed to analyze asset: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvancedAnalysis = async (assetId: string) => {
    setAdvancedAnalysisLoading(true);
    try {
      const [frontendUsage, dataSource, displayMapping, debugMetadata] = await Promise.all([
        axiosInstance.get<FrontendUsageAnalysis>(`/api/field-diagnostics/frontend-usage/${assetId}`),
        axiosInstance.get<DataSourceAnalysis>(`/api/field-diagnostics/data-sources/${assetId}`),
        axiosInstance.get<DisplayMappingAnalysis>(`/api/field-diagnostics/display-mapping/${assetId}`),
        axiosInstance.get<DebugMetadataAnalysis>(`/api/field-diagnostics/debug-metadata/${assetId}`)
      ]);

      setFrontendUsageData(frontendUsage.data);
      setDataSourceData(dataSource.data);
      setDisplayMappingData(displayMapping.data);
      setDebugMetadataData(debugMetadata.data);
    } catch (err: any) {
      console.error('Error fetching advanced analysis:', err);
      setError(`Failed to fetch advanced analysis: ${err.message}`);
    } finally {
      setAdvancedAnalysisLoading(false);
    }
  };

  const loadSystemHealth = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get('/api/field-diagnostics/system-health');
      setSystemHealth(response.data.systemHealth);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load system health data');
    } finally {
      setLoading(false);
    }
  };

  const loadMappingConflicts = async () => {
    setMappingConflictsLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get('/api/field-diagnostics/mapping-conflicts');
      setMappingConflictsData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load mapping conflicts analysis');
    } finally {
      setMappingConflictsLoading(false);
    }
  };

  React.useEffect(() => {
    if (activeTab === 1) {
      loadSystemHealth();
    }
  }, [activeTab]);

  const renderSearchTab = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Sök Asset för Field Analys
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sök med artikelnummer, GTIN, filnamn, titel eller Asset ID
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="t.ex. 7311311000055, Grillost, asset_id..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              Sök
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Validoo Probe: Testa GTIN mot alla kända Validoo-fält */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🔎 Validoo Field Probe (GTIN)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Mata in ett GTIN och se vilket värde som returneras för utvalda Validoo‑fält, inklusive "with_unit"‑aggregationer
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="GTIN, t.ex. 0731615077581"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              variant="outlined"
              onClick={async () => {
                if (!searchQuery.trim()) { setError('Ange GTIN'); return; }
                setValidooLoading(true); setError(null);
                try {
                  const resp = await axiosInstance.get(`/api/validoo/fields/all`, { params: { gtin: searchQuery } });
                  const fields: Array<{path: string, label: string, example: string}> = resp.data.fields || [];
                  setValidooRawFields(fields);
                  // Helpers to combine measurement + unit
                  const get = (p: string) => fields.find(f => f.path.toLowerCase().includes(p))?.example || '';
                  const unitMap: Record<string, string> = { 'GRM': 'g', 'MLT': 'ml', 'MMT': 'mm', 'E14': 'kJ', 'KJO': 'kJ' };
                  const combine = (v: string, u: string) => v ? `${v} ${unitMap[u] || u || ''}`.trim() : '';
                  const computed: Record<string, string> = {
                    validoo_price_comparison_with_unit: combine(get('pricecomparisonmeasurement._'), get('pricecomparisonmeasurement.measurementunitcode')),
                    validoo_price_comparison_content_type: get('pricecomparisoncontenttypecode._'),
                    validoo_drained_weight_with_unit: combine(get('tradeitemweight.drainedweight._'), get('drainedweight.measurementunitcode')),
                    validoo_drained_weight: get('tradeitemweight.drainedweight._'),
                    validoo_net_content_with_unit: combine(get('netcontent._'), get('netcontent.measurementunitcode')),
                    validoo_net_content: get('netcontent._'),
                    validoo_gross_weight_with_unit: combine(get('grossweight._'), get('grossweight.measurementunitcode')),
                    validoo_gross_weight: get('grossweight._')
                  };
                  setValidooComputed(computed);
                } catch (err: any) {
                  setError(err.response?.data?.error || err.message || 'Validoo probe failed');
                } finally {
                  setValidooLoading(false);
                }
              }}
              disabled={validooLoading}
            >
              Kör Probe
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
            <TextField
              size="small"
              placeholder="Filtrera raw fält (t.ex. drained, measurement, allergen)"
              value={validooFilter}
              onChange={(e) => setValidooFilter(e.target.value)}
              sx={{ maxWidth: 400 }}
            />
          </Box>
          <Grid container spacing={2}>
            <Box sx={{ width: { xs: '100%', md: '41.6667%' } }}>
              <Typography variant="subtitle1" gutterBottom>Validoo – Computed helpers</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(validooComputed).map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{k}</TableCell>
                        <TableCell>{v || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box sx={{ width: { xs: '100%', md: '58.3333%' } }}>
              <Typography variant="subtitle1" gutterBottom>Validoo – Raw fields (unfiltered)</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Path</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validooRawFields
                      .filter(f => !validooFilter || f.path.toLowerCase().includes(validooFilter.toLowerCase()))
                      .map((f, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{f.path}</TableCell>
                          <TableCell>{f.example}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {searchResults.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sökresultat ({searchResults.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Titel</TableCell>
                    <TableCell>Filnamn</TableCell>
                    <TableCell>Kategorier</TableCell>
                    <TableCell>Metadata Fält</TableCell>
                    <TableCell>Åtgärd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((asset) => (
                    <TableRow key={asset._id}>
                      <TableCell>{asset.title || 'Ingen titel'}</TableCell>
                      <TableCell>{(() => { try { return decodeURIComponent((asset.fileName || '').replace(/\+/g,' ')); } catch { return asset.fileName; } })()}</TableCell>
                      <TableCell>{asset.categories?.length || 0} kategorier</TableCell>
                      <TableCell>
                        <Chip 
                          label={`${asset.metadataCount} fält`} 
                          color={asset.metadataCount > 0 ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => analyzeAsset(asset._id)}
                          startIcon={<BugReportIcon />}
                        >
                          Analysera
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedAssetAnalysis && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <BugReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Detaljerad Field Analys: {selectedAssetAnalysis.asset.title || (() => { try { return decodeURIComponent((selectedAssetAnalysis.asset.fileName||'').replace(/\+/g,' ')); } catch { return selectedAssetAnalysis.asset.fileName; } })()}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Asset Information</Typography>
                  <Typography variant="body2"><strong>ID:</strong> {selectedAssetAnalysis.asset._id}</Typography>
                  <Typography variant="body2"><strong>Filnamn:</strong> {(() => { try { return decodeURIComponent((selectedAssetAnalysis.asset.fileName||'').replace(/\+/g,' ')); } catch { return selectedAssetAnalysis.asset.fileName; } })()}</Typography>
                  <Typography variant="body2"><strong>Filtyp:</strong> {selectedAssetAnalysis.asset.fileType}</Typography>
                  <Typography variant="body2"><strong>Kategorier:</strong> {selectedAssetAnalysis.asset.categories?.length || 0}</Typography>
                </Paper>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Field Statistik</Typography>
                  <Typography variant="body2">
                    <strong>Totalt metadata fält:</strong> {selectedAssetAnalysis.fieldAnalysis.totalMetadataFields}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Orphaned fält:</strong> 
                    <Chip 
                      label={selectedAssetAnalysis.fieldAnalysis.orphanedFields}
                      color={selectedAssetAnalysis.fieldAnalysis.orphanedFields > 0 ? 'warning' : 'success'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="body2">
                    <strong>Matchande templates:</strong> {selectedAssetAnalysis.fieldAnalysis.matchingTemplates.length}
                  </Typography>
                </Paper>
              </Box>
            </Box>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Metadata Fält ({selectedAssetAnalysis.fieldAnalysis.metadataFields.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field Key</TableCell>
                        <TableCell>Värde (förkortad)</TableCell>
                        <TableCell>Typ</TableCell>
                        <TableCell>Template Match</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedAssetAnalysis.fieldAnalysis.metadataFields.map((field, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {field.key}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {String(field.value)}
                            </Typography>
                            {field.valueLength && (
                              <Typography variant="caption" color="text.secondary">
                                ({field.valueLength} tecken)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={field.valueType} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {field.foundInTemplates.length > 0 ? (
                              field.foundInTemplates.map((template, idx) => (
                                <Chip
                                  key={idx}
                                  label={`${template.templateName}: ${template.fieldLabel}`}
                                  size="small"
                                  color="primary"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">Ingen match</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {field.isOrphaned ? (
                              <Chip label="Orphaned" color="warning" size="small" icon={<WarningIcon />} />
                            ) : (
                              <Chip label="Matched" color="success" size="small" icon={<CheckCircleIcon />} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            {selectedAssetAnalysis.fieldAnalysis.matchingTemplates.length > 0 && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Template Coverage ({selectedAssetAnalysis.fieldAnalysis.matchingTemplates.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedAssetAnalysis.fieldAnalysis.matchingTemplates.map((template, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {template.templateName} ({template.templateFieldCount} fält)
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Field ID</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Label</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Har Värde</TableCell>
                              <TableCell>Värde</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {template.fields.map((field, fieldIndex) => (
                              <TableRow key={fieldIndex}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                  {field.id}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                  {field.name}
                                </TableCell>
                                <TableCell>{field.label}</TableCell>
                                <TableCell>
                                  <Chip label={field.type} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  {field.hasValue ? (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  ) : (
                                    <WarningIcon color="warning" fontSize="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {field.hasValue ? (
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                      {String(field.value)}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      Inget värde
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderSystemHealthTab = () => (
    <Box>
      {systemHealth && (
        <>
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{systemHealth.totalAssets}</Typography>
                  <Typography variant="body2">Totalt Assets</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary">{systemHealth.totalTemplates}</Typography>
                  <Typography variant="body2">Templates</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">{systemHealth.uniqueMetadataFields}</Typography>
                  <Typography variant="body2">Unika Metadata Fält</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h4" 
                    color={systemHealth.healthScore > 80 ? 'success.main' : systemHealth.healthScore > 60 ? 'warning.main' : 'error.main'}
                  >
                    {systemHealth.healthScore}%
                  </Typography>
                  <Typography variant="body2">Hälso-poäng</Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Field Health Översikt
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography variant="body2">
                    <strong>Assets med metadata:</strong> {systemHealth.assetsWithMetadata}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Template fält:</strong> {systemHealth.uniqueTemplateFields}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography variant="body2">
                    <strong>Orphaned fält:</strong> 
                    <Chip 
                      label={systemHealth.orphanedFieldsCount}
                      color={systemHealth.orphanedFieldsCount > 0 ? 'warning' : 'success'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {systemHealth.orphanedFields.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <WarningIcon color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Orphaned Fields (visar första 20)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Dessa fält finns i asset metadata men matchar ingen template field
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {systemHealth.orphanedFields.map((field, index) => (
                    <Chip
                      key={index}
                      label={field}
                      variant="outlined"
                      color="warning"
                      size="small"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );

  const renderFrontendUsageTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        🔍 Frontend Usage Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Analyserar exakt hur fält används i olika frontend-komponenter
      </Typography>

      {advancedAnalysisLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {frontendUsageData && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {frontendUsageData.summary.assetDetailsFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AssetDetails Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" color="secondary">
                  {frontendUsageData.summary.collectionViewFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CollectionView Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {frontendUsageData.summary.csvMappedFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CSV Mapped Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {frontendUsageData.summary.orphanedKeys}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Orphaned Keys
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">AssetDetails.tsx</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Render Method:</strong> {frontendUsageData.frontendUsageAnalysis.assetDetails.renderMethod}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Field Source:</strong> {frontendUsageData.frontendUsageAnalysis.assetDetails.fieldSource}
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Fields Used ({frontendUsageData.frontendUsageAnalysis.assetDetails.fieldsUsed.length})
                  </Typography>
                  {frontendUsageData.frontendUsageAnalysis.assetDetails.fieldsUsed.map((field, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {field.fieldLabel || field.fieldName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Key: {field.fieldKey} | Template: {field.templateName}
                      </Typography>
                    </Paper>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">CollectionDetailView.tsx</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Render Method:</strong> {frontendUsageData.frontendUsageAnalysis.collectionDetailView.renderMethod}
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Fields Rendered ({frontendUsageData.frontendUsageAnalysis.collectionDetailView.fieldsUsed.length})
                  </Typography>
                  {frontendUsageData.frontendUsageAnalysis.collectionDetailView.fieldsUsed.map((field, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {field.displayLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Key: {field.fieldKey} | Has Template Label: {field.hasTemplateLabel ? 'Yes' : 'No'}
                      </Typography>
                    </Paper>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">CSV Import/Export</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Render Method:</strong> {frontendUsageData.frontendUsageAnalysis.csvMapping.renderMethod}
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    CSV Mapped Fields ({frontendUsageData.frontendUsageAnalysis.csvMapping.fieldsUsed.length})
                  </Typography>
                  {frontendUsageData.frontendUsageAnalysis.csvMapping.fieldsUsed.map((field, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {field.fieldLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        CSV ID: {field.csvFieldId} | Template: {field.templateName}
                      </Typography>
                    </Paper>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );

  const renderDataSourceTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        📊 Data Source Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Analyserar varifrån fältdata kommer (Validoo, CSV, Manual edit)
      </Typography>

      {advancedAnalysisLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {dataSourceData && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {dataSourceData.summary.validooFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Validoo Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="secondary">
                  {dataSourceData.summary.csvFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CSV Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {dataSourceData.summary.manualFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manual Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {dataSourceData.summary.unknownFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unknown Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6">
                  {dataSourceData.summary.totalFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Fields
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Field Source Analysis</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field Key</TableCell>
                      <TableCell>Probable Source</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Indicators</TableCell>
                      <TableCell>Current Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.values(dataSourceData.dataSourceAnalysis.fieldSourceAnalysis).map((field) => (
                      <TableRow key={field.fieldKey}>
                        <TableCell>{field.fieldKey}</TableCell>
                        <TableCell>
                          <Chip 
                            label={field.probableSource} 
                            size="small"
                            color={
                              field.probableSource === 'validoo' ? 'primary' :
                              field.probableSource === 'csv_import' ? 'secondary' :
                              field.probableSource === 'manual_edit' ? 'info' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={field.confidence} 
                            size="small"
                            color={field.confidence === 'high' ? 'success' : field.confidence === 'medium' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 200 }}>
                            {field.indicators.map((indicator, index) => (
                              <Chip key={index} label={indicator} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {typeof field.currentValue === 'string' ? 
                              field.currentValue.substring(0, 50) + (field.currentValue.length > 50 ? '...' : '') :
                              String(field.currentValue)
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Timeline</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {dataSourceData.dataSourceAnalysis.timeline.map((event, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Typography variant="subtitle2">{event.event}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(event.timestamp).toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    Source: {event.source}
                    {event.fieldsAdded && ` | Fields Added: ${event.fieldsAdded}`}
                    {event.fieldsModified && ` | Fields Modified: ${event.fieldsModified}`}
                  </Typography>
                </Paper>
              ))}
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );

  const renderDisplayMappingTab = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <Button 
          variant="contained" 
          onClick={() => selectedAssetAnalysis && fetchAdvancedAnalysis(selectedAssetAnalysis.asset._id)}
          disabled={!selectedAssetAnalysis || advancedAnalysisLoading}
          startIcon={advancedAnalysisLoading ? <CircularProgress size={20} /> : <AssessmentIcon />}
        >
          {advancedAnalysisLoading ? 'Analyserar...' : 'Analysera Display Mapping'}
        </Button>
        {selectedAssetAnalysis && (
          <Typography variant="body2" color="text.secondary">
            Asset: {selectedAssetAnalysis.asset.title}
          </Typography>
        )}
      </Box>

      {displayMappingData && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {displayMappingData.summary.totalFields}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {displayMappingData.summary.visibleInAssetDetails}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AssetDetails Visible
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {displayMappingData.summary.visibleInCollectionView}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CollectionView Visible
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {displayMappingData.summary.renderingIssues}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rendering Issues
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Component Comparison</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle1" color="info.main" gutterBottom>
                    Only in AssetDetails ({displayMappingData.displayMappingAnalysis.componentComparison.onlyInAssetDetails.length})
                  </Typography>
                  {displayMappingData.displayMappingAnalysis.componentComparison.onlyInAssetDetails.map((field, index) => (
                    <Chip key={index} label={field} size="small" color="info" variant="outlined" />
                  ))}
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle1" color="secondary.main" gutterBottom>
                    Only in CollectionView ({displayMappingData.displayMappingAnalysis.componentComparison.onlyInCollectionView.length})
                  </Typography>
                  {displayMappingData.displayMappingAnalysis.componentComparison.onlyInCollectionView.map((field, index) => (
                    <Chip key={index} label={field} size="small" color="secondary" variant="outlined" />
                  ))}
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle1" color="success.main" gutterBottom>
                    Common Fields ({displayMappingData.displayMappingAnalysis.componentComparison.commonFields.length})
                  </Typography>
                  {displayMappingData.displayMappingAnalysis.componentComparison.commonFields.slice(0, 10).map((field, index) => (
                    <Chip key={index} label={field} size="small" color="success" variant="outlined" />
                  ))}
                  {displayMappingData.displayMappingAnalysis.componentComparison.commonFields.length > 10 && (
                    <Chip label={`+${displayMappingData.displayMappingAnalysis.componentComparison.commonFields.length - 10} more`} size="small" />
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {displayMappingData.displayMappingAnalysis.renderingIssues.length > 0 && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" color="warning.main">
                  Rendering Issues ({displayMappingData.displayMappingAnalysis.renderingIssues.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {displayMappingData.displayMappingAnalysis.renderingIssues.map((issue, index) => (
                  <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">{issue.fieldKey}</Typography>
                    <Typography variant="body2">{issue.issue}</Typography>
                    <Typography variant="caption" color="text.secondary">Impact: {issue.impact}</Typography>
                  </Alert>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Field Display Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field Key</TableCell>
                      <TableCell>Value Preview</TableCell>
                      <TableCell>AssetDetails</TableCell>
                      <TableCell>CollectionView</TableCell>
                      <TableCell>CSV Export</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(displayMappingData.displayMappingAnalysis.fieldDisplayMap).slice(0, 20).map(([fieldKey, displayInfo]) => (
                      <TableRow key={fieldKey}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {fieldKey}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          {displayInfo.value ? (
                            <Typography variant="body2" sx={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              fontSize: '0.75rem'
                            }}>
                              {typeof displayInfo.value === 'string' ? 
                                displayInfo.value.substring(0, 30) + (displayInfo.value.length > 30 ? '...' : '') :
                                String(displayInfo.value)
                              }
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              No value
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {displayInfo.assetDetailsDisplay.isVisible ? (
                            <Chip label={displayInfo.assetDetailsDisplay.displayLabel || 'Visible'} size="small" color="success" />
                          ) : (
                            <Chip label="Hidden" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          {displayInfo.collectionViewDisplay.isVisible ? (
                            <Chip label={displayInfo.collectionViewDisplay.displayLabel || 'Visible'} size="small" color="info" />
                          ) : (
                            <Chip label="Hidden" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          {displayInfo.csvExportDisplay.isVisible ? (
                            <Chip label={displayInfo.csvExportDisplay.displayLabel || 'Exported'} size="small" color="secondary" />
                          ) : (
                            <Chip label="Not exported" size="small" color="default" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );

  const renderMappingConflictsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <Button 
          variant="contained" 
          onClick={loadMappingConflicts}
          disabled={mappingConflictsLoading}
          startIcon={mappingConflictsLoading ? <CircularProgress size={20} /> : <WarningIcon />}
          color="warning"
        >
          {mappingConflictsLoading ? 'Analyserar...' : 'Analysera Mappning-konflikter'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          Visar duplikerade mappningar mellan Validoo-fält och template-fält
        </Typography>
      </Box>

      {mappingConflictsData && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {mappingConflictsData.mappingAnalysis.totalMappings}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Mappningar
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  {mappingConflictsData.mappingAnalysis.duplicates}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dubbletter
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {mappingConflictsData.mappingAnalysis.priorityConflicts.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prioritets-konflikter
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {mappingConflictsData.mappingAnalysis.cleanMappings.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rena Mappningar
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {mappingConflictsData.mappingAnalysis.duplicateDetails.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" color="error.main">
                  🚨 Dubbletter & Konflikter ({mappingConflictsData.mappingAnalysis.duplicateDetails.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {mappingConflictsData.mappingAnalysis.duplicateDetails.map((duplicate, index) => (
                  <Card key={index} sx={{ mb: 2, border: duplicate.riskLevel === 'HIGH' ? '2px solid red' : duplicate.riskLevel === 'MEDIUM' ? '2px solid orange' : '1px solid gray' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h3">
                          Template Field: <code>{duplicate.templateField}</code>
                        </Typography>
                        <Chip 
                          label={`${duplicate.riskLevel} RISK`} 
                          color={duplicate.riskLevel === 'HIGH' ? 'error' : duplicate.riskLevel === 'MEDIUM' ? 'warning' : 'default'}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {duplicate.count} Validoo-fält mappar till samma template-fält:
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {duplicate.mappingTypes.map((mapping, idx) => (
                          <Chip
                            key={idx}
                            label={`${mapping.validooField} (${mapping.source}${mapping.priority ? ` - Prioritet: ${mapping.priority}` : ''})`}
                            size="small"
                            color={mapping.source === 'HARDCODED' ? 'primary' : mapping.source === 'DROPDOWN' ? 'secondary' : 'default'}
                            variant={mapping.isActiveInDropdown ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Box>

                      <Alert severity={duplicate.riskLevel === 'HIGH' ? 'error' : duplicate.riskLevel === 'MEDIUM' ? 'warning' : 'info'}>
                        <Typography variant="body2">
                          <strong>Rekommendation:</strong> {duplicate.recommendedAction}
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {mappingConflictsData.mappingAnalysis.priorityConflicts.length > 0 && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" color="warning.main">
                  ⚡ Prioritets-konflikter ({mappingConflictsData.mappingAnalysis.priorityConflicts.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Fält där både hårdkodad mapping och dropdown-val finns:
                </Typography>
                {mappingConflictsData.mappingAnalysis.priorityConflicts.map((conflict, index) => (
                  <Card key={index} sx={{ mb: 2, bgcolor: 'warning.light' }}>
                    <CardContent>
                      <Typography variant="h6">
                        Template Field: <code>{conflict.templateField}</code>
                      </Typography>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Hårdkodad:</strong> {conflict.hardcodedMapping}
                        </Typography>
                        {conflict.dropdownMapping && (
                          <Typography variant="body2">
                            <strong>Dropdown:</strong> {conflict.dropdownMapping}
                          </Typography>
                        )}
                        <Typography variant="body2" color="primary">
                          <strong>Aktuell vinnare:</strong> {conflict.currentWinner}
                        </Typography>
                      </Box>
                      <Alert severity="info">
                        <Typography variant="body2">
                          <strong>Rekommendation:</strong> {conflict.recommendation}
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color="success.main">
                ✅ Rena Mappningar ({mappingConflictsData.mappingAnalysis.cleanMappings.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fält utan konflikter (1:1 mappning):
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Validoo Field</TableCell>
                      <TableCell>Template Field</TableCell>
                      <TableCell>Källa</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappingConflictsData.mappingAnalysis.cleanMappings.slice(0, 20).map((mapping, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {mapping.validooField}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {mapping.templateField}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={mapping.source} 
                            size="small" 
                            color={mapping.source === 'HARDCODED' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {mappingConflictsData.mappingAnalysis.cleanMappings.length > 20 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  ... och {mappingConflictsData.mappingAnalysis.cleanMappings.length - 20} fler
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">🔧 System Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" gutterBottom>Template Information</Typography>
                  <Typography variant="body2">Name: {mappingConflictsData.systemInfo.template}</Typography>
                  <Typography variant="body2">ID: {mappingConflictsData.systemInfo.templateId}</Typography>
                  <Typography variant="body2">Total Fields: {mappingConflictsData.systemInfo.totalTemplateFields}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" gutterBottom>Enabled Integrations</Typography>
                  {mappingConflictsData.systemInfo.enabledIntegrations.map((integration, index) => (
                    <Chip key={index} label={integration} size="small" color="primary" />
                  ))}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );

  const renderDebugMetadataTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        🔧 Debug Metadata Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Detaljerad analys av field matching logik och exakt vilka fält som hittas var
      </Typography>

      {advancedAnalysisLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {debugMetadataData && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {debugMetadataData.metadataKeysCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Metadata Keys
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="secondary">
                  {debugMetadataData.fieldMatchingAnalysis.templateFieldsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Template Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {debugMetadataData.fieldMatchingAnalysis.matchingFieldsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Matching Fields
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {debugMetadataData.fieldMatchingAnalysis.orphanedKeysCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Orphaned Keys
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Raw Metadata Keys</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>
                Exakt vilka nycklar som finns i asset.metadata:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {debugMetadataData.metadataKeys.map((key, index) => (
                  <Chip
                    key={index}
                    label={key}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {debugMetadataData.matchingTemplates.map((template, templateIndex) => (
            <Accordion key={templateIndex} sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Template: {template.templateName} ({template.fields.length} fält)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Visar exakt field matching logik som TemplateFieldsForm använder
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field ID</TableCell>
                        <TableCell>Field Name</TableCell>
                        <TableCell>Field Label</TableCell>
                        <TableCell>Found by ID?</TableCell>
                        <TableCell>Found by Name?</TableCell>
                        <TableCell>Resolved Value</TableCell>
                        <TableCell>Has Value?</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {template.fields.map((field, fieldIndex) => (
                        <TableRow 
                          key={fieldIndex}
                          sx={{ 
                            backgroundColor: field.hasResolvedValue ? 'success.light' : 'grey.100',
                            '&:hover': { backgroundColor: field.hasResolvedValue ? 'success.main' : 'grey.200' }
                          }}
                        >
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {field.fieldId}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {field.fieldName || 'N/A'}
                          </TableCell>
                          <TableCell>{field.fieldLabel}</TableCell>
                          <TableCell>
                            {field.valueFoundByFieldId ? (
                              <Chip label="YES" size="small" color="success" />
                            ) : (
                              <Chip label="NO" size="small" color="error" />
                            )}
                          </TableCell>
                          <TableCell>
                            {field.valueFoundByFieldName ? (
                              <Chip label="YES" size="small" color="success" />
                            ) : (
                              <Chip label="NO" size="small" color="error" />
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            {field.resolvedValue ? (
                              <Typography variant="body2" sx={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                backgroundColor: 'success.light',
                                padding: '2px 4px',
                                borderRadius: '4px'
                              }}>
                                {typeof field.resolvedValue === 'string' ? 
                                  field.resolvedValue.substring(0, 50) + (field.resolvedValue.length > 50 ? '...' : '') :
                                  String(field.resolvedValue)
                                }
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No value
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {field.hasResolvedValue ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <WarningIcon color="warning" fontSize="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          🔧 Field Diagnostics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Avancerad analys av fält och metadata i DAM-systemet
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Asset Search & Analysis" />
          <Tab label="System Health" />
          <Tab label="Frontend Usage" />
          <Tab label="Data Sources" />
          <Tab label="Display Mapping" />
          <Tab label="Mapping Conflicts" />
          <Tab label="Debug Metadata" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {activeTab === 0 && renderSearchTab()}
      {activeTab === 1 && renderSystemHealthTab()}
      {activeTab === 2 && renderFrontendUsageTab()}
      {activeTab === 3 && renderDataSourceTab()}
      {activeTab === 4 && renderDisplayMappingTab()}
      {activeTab === 5 && renderMappingConflictsTab()}
      {activeTab === 6 && renderDebugMetadataTab()}
    </Container>
  );
};

export default FieldDiagnostics; 